/**
 * Drawing of the collapsed motion columns (W2-04a motion-columns; DESIGN.md §4.7 wireframe, §5.6, §6.7, §6.9):
 *
 *   static   background, beat grid (the host's meter and snap colours), the gap between paired lanes;
 *   content  per lane: focus tint, the opacity "< 0" zone of the value axis, hatched bands where Phira hides the
 *            line's notes (opacity < 0), colour bands, selected segments, curves (guide, Σ dashed, main), "Stay,
 *            then jump" dashed lines, jump steps, keys (filled when selected) and half-diamond jump keys, link
 *            chains, value labels (one per key; the arrive and leave values, emphasised, only at jumps, UX-032),
 *            easing and status badges, Phira-default ghost keys, raw events. Everything here is placed by beats only,
 *            so the host may draw it for a taller strip and scroll the pixels (TimelineHost scroll cache);
 *   fixed    what stays put on the visible area (`drawMotionFixed`): value-axis labels at the top, lane labels at
 *            the bottom, the "Not set" text of an absent track, the pair link chain and the column message;
 *   overlay  hover ring, RPE segment-placement ghost, paste ghost keys, the dragged key's readout, the value at
 *            the playhead.
 *
 * Every colour comes from the design tokens: `readMotionTheme` reads the motion/curve tokens at the canvas (curve
 * tokens of W1-12) and falls back to the timeline theme the host resolved; nothing here is a literal colour.
 * Shapes accompany colours everywhere (filled vs hollow diamonds, dashed holds, hatching, text badges), so colour
 * is never the only signal.
 */

import type { RGB } from '../../../core/util/types';
import type { TimeAxis } from '../../types';
import { axisContext } from '../axis';
import { strokeSampledCurve } from '../curveCache';
import { Glyphs, hatchRect } from '../glyphs';
import { drawBeatGrid, fitText } from '../gutterColumn';
import { canvasFont, FALLBACK_THEME, type TimelineTheme } from '../theme';
import { keyX, laneRects, MOTION_KEY_SIZE, visibleKeyRange, type LaneRect } from './motionHit';
import type {
  MotionBadgeTone,
  MotionColumnScene,
  MotionKeyView,
  MotionLaneScene,
  MotionOverlayScene,
} from './motionScene';

// ---------------------------------------------------------------------------------------------------------------
// Theme
// ---------------------------------------------------------------------------------------------------------------

export interface MotionTheme {
  readonly base: TimelineTheme;
  readonly curve: string;
  readonly curveGuide: string;
  readonly overshoot: string;
  readonly keyFill: string;
  readonly keySelected: string;
  readonly keyStroke: string;
  readonly hold: string;
  readonly focus: string;
  readonly selection: string;
  readonly label: string;
  readonly labelBg: string;
  readonly link: string;
  readonly danger: string;
  readonly dangerMuted: string;
  readonly ghost: string;
  readonly badge: Readonly<Record<MotionBadgeTone, { readonly bg: string; readonly fg: string }>>;
}

type Reader = (name: string) => string;

function pick(read: Reader, names: readonly string[], fallback: string): string {
  for (const n of names) {
    const v = read(n).trim();
    if (v !== '') return v;
  }
  return fallback;
}

/** Builds the motion theme from a token reader and the host's timeline theme (tests pass a fake reader). */
export function motionThemeFromTokens(base: TimelineTheme, read: Reader): MotionTheme {
  const t = (names: readonly string[], fallback: string): string => pick(read, names, fallback);
  return Object.freeze({
    base,
    curve: t(['--color-curve'], base.accent),
    curveGuide: t(['--color-curve-guide'], base.textSubtle),
    overshoot: t(['--color-curve-overshoot'], base.severity.warning),
    keyFill: t(['--color-surface-1', '--color-bg'], base.background),
    keySelected: t(['--color-accent'], base.accent),
    keyStroke: t(['--color-text'], base.text),
    hold: t(['--color-text-muted'], base.textMuted),
    focus: t(['--color-accent-muted'], base.accentMuted),
    selection: t(['--color-selection'], base.selection),
    label: t(['--color-text-muted'], base.textMuted),
    labelBg: t(['--color-surface-2', '--color-bg'], base.background),
    link: t(['--color-warning'], base.severity.warning),
    danger: t(['--color-danger'], base.severity.error),
    dangerMuted: t(['--color-danger-muted'], base.severity.error),
    ghost: t(['--color-text-subtle'], base.textSubtle),
    badge: Object.freeze({
      neutral: {
        bg: t(['--color-surface-3', '--color-surface-2'], base.background),
        fg: t(['--color-text-muted'], base.textMuted),
      },
      warning: { bg: t(['--color-warning-muted'], base.background), fg: t(['--color-warning'], base.severity.warning) },
      baked: { bg: t(['--color-badge-baked-muted'], base.background), fg: t(['--color-badge-baked'], base.textMuted) },
      info: { bg: t(['--color-info-muted'], base.background), fg: t(['--color-info'], base.severity.tip) },
      danger: { bg: t(['--color-danger-muted'], base.background), fg: t(['--color-danger'], base.severity.error) },
    }),
  });
}

const themeCache = new WeakMap<TimelineTheme, MotionTheme>();

/** The motion theme for the host's current timeline theme, reading tokens at `el` (cached per host theme). */
export function readMotionTheme(base: TimelineTheme, el: Element | null): MotionTheme {
  const hit = themeCache.get(base);
  if (hit !== undefined) return hit;
  let style: CSSStyleDeclaration | null = null;
  if (el !== null && typeof getComputedStyle === 'function') {
    try {
      style = getComputedStyle(el);
    } catch {
      style = null;
    }
  }
  const theme = motionThemeFromTokens(base, (name) => style?.getPropertyValue(name) ?? '');
  themeCache.set(base, theme);
  return theme;
}

/** The theme for a draw call: the host's theme from the axis context, tokens read at the canvas. */
export function themeFor(ctx: CanvasRenderingContext2D, axis: TimeAxis): MotionTheme {
  const base = axisContext(axis)?.theme ?? FALLBACK_THEME;
  const canvas = (ctx as Partial<Pick<CanvasRenderingContext2D, 'canvas'>>).canvas;
  const el = typeof Element !== 'undefined' && canvas instanceof Element ? canvas : null;
  return readMotionTheme(base, el);
}

// ---------------------------------------------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------------------------------------------

/** Below these heights a segment gets no value labels / badges. */
const LABEL_SEGMENT_PX = 30;
const BADGE_SEGMENT_PX = 16;
const BADGE_H = 12;
const LABEL_FONT_PX = 9;
/** Hatched bands taller than this repeat their label at this spacing (CSS px). */
const BAND_LABEL_EVERY_PX = 480;

export function rgbCss(c: RGB, alpha = 1): string {
  const ch = (v: number): number => Math.min(255, Math.max(0, Math.round(v)));
  return `rgb(${String(ch(c[0]))} ${String(ch(c[1]))} ${String(ch(c[2]))} / ${String(alpha)})`;
}

function clipRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number): void {
  ctx.beginPath();
  ctx.rect(x, y, w, h);
  ctx.clip();
}

/** A text label with a background plate; returns the plate width. */
function plateText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxW: number,
  fg: string,
  bg: string,
  align: 'left' | 'right' | 'center',
): number {
  if (text === '' || maxW < 8) return 0;
  const shown = fitText(ctx, text, maxW - 4);
  if (shown === '') return 0;
  const w = Math.min(maxW, ctx.measureText(shown).width + 4);
  const left = align === 'left' ? x : align === 'right' ? x - w : x - w / 2;
  ctx.fillStyle = bg;
  ctx.globalAlpha = 0.85;
  ctx.fillRect(left, y - LABEL_FONT_PX / 2 - 2, w, LABEL_FONT_PX + 3);
  ctx.globalAlpha = 1;
  ctx.fillStyle = fg;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(shown, left + 2, y);
  return w;
}

/** Room kept for the high value of a narrow value-range row (px). */
const RANGE_HI_STUB_PX = 14;

/** Splits a formatted value into its number and its unit suffix ("-23 px" -> ["-23", " px"]; no digits: no unit). */
export function splitUnit(text: string): readonly [string, string] {
  const m = /^(.*\d)(\D*)$/su.exec(text);
  return m === null ? [text, ''] : [m[1] ?? text, m[2] ?? ''];
}

/**
 * The value-range row of a lane (CR-W3-05-2 item 1): the low value at the left, the high value with the unit at the
 * right ("-23   320 px"), so the row reads as a range with its unit; the unit is printed once. When the row is
 * narrow the high value's unit goes first, then the high value is shortened; the low value is shortened last.
 * Widths include the plates' 4 px padding.
 */
export function fitRangeLabels(
  ctx: CanvasRenderingContext2D,
  loText: string,
  hiText: string,
  availPx: number,
): { readonly lo: string; readonly hi: string; readonly loW: number; readonly hiW: number } {
  const [loNum, loUnit] = splitUnit(loText);
  const [hiNum, hiUnit] = splitUnit(hiText);
  const lo = loUnit !== '' && loUnit === hiUnit ? loNum : loText;
  const needLo = ctx.measureText(lo).width + 4;
  const needHi = ctx.measureText(hiText).width + 4;
  if (needLo + needHi <= availPx) return { lo, hi: hiText, loW: needLo, hiW: needHi };
  const needHiNum = ctx.measureText(hiNum).width + 4;
  if (needLo + needHiNum <= availPx) return { lo, hi: hiNum, loW: needLo, hiW: needHiNum };
  // The low value keeps its whole width while the high value still gets a stub (a digit and the ellipsis).
  const loW = Math.min(needLo, Math.max(availPx / 2, availPx - RANGE_HI_STUB_PX));
  return { lo, hi: hiNum, loW, hiW: Math.max(0, availPx - loW) };
}

function diamond(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  selected: boolean,
  theme: MotionTheme,
  size = MOTION_KEY_SIZE,
): void {
  ctx.fillStyle = selected ? theme.keySelected : theme.keyFill;
  ctx.strokeStyle = selected ? theme.keySelected : theme.curve;
  ctx.lineWidth = 1.5;
  Glyphs.keyDiamond(ctx, x, y, size, true);
  if (selected) {
    // A selected key also gets a contrasting outline (not colour alone).
    ctx.strokeStyle = theme.keyStroke;
    ctx.lineWidth = 1;
    Glyphs.keyDiamond(ctx, x, y, size + 3, false);
  }
}

/** A small chain of two links: segments in a link group (DESIGN.md §5.6.5 "gold chain glyph"). */
function chain(ctx: CanvasRenderingContext2D, x: number, y: number, color: string): void {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  for (const dx of [-2.5, 2.5]) {
    ctx.beginPath();
    ctx.ellipse(x + dx, y, 3.2, 2, 0, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.restore();
}

function laneVisible(lane: MotionLaneScene, axis: TimeAxis): readonly [number, number] {
  const [b0, b1] = axis.range;
  return visibleKeyRange(lane.keys, b0, b1);
}

// ---------------------------------------------------------------------------------------------------------------
// Static layer
// ---------------------------------------------------------------------------------------------------------------

export function drawMotionStatic(
  ctx: CanvasRenderingContext2D,
  axis: TimeAxis,
  widthPx: number,
  scene: MotionColumnScene,
  theme: MotionTheme,
  snapDiv: number,
): void {
  const h = axis.heightPx;
  ctx.fillStyle = theme.base.background;
  ctx.fillRect(0, 0, widthPx, h);
  const grid = axisContext(axis);
  drawBeatGrid(ctx, axis, widthPx, {
    div: scene.compact ? 1 : snapDiv,
    meter: grid?.meter ?? null,
    theme: theme.base,
    subAlpha: 0.18,
    beatAlpha: 0.7,
  });
  const rects = laneRects(scene, widthPx);
  for (let i = 1; i < rects.length; i++) {
    const prev = rects[i - 1];
    const cur = rects[i];
    if (prev === undefined || cur === undefined) continue;
    ctx.fillStyle = theme.base.gutterBackground;
    ctx.fillRect(prev.x1, 0, cur.x0 - prev.x1, h);
    ctx.fillStyle = theme.base.divider;
    ctx.fillRect(Math.round((prev.x1 + cur.x0) / 2), 0, 1, h);
  }
}

// ---------------------------------------------------------------------------------------------------------------
// Content layer
// ---------------------------------------------------------------------------------------------------------------

export function drawMotionContent(
  ctx: CanvasRenderingContext2D,
  axis: TimeAxis,
  widthPx: number,
  scene: MotionColumnScene,
  theme: MotionTheme,
): void {
  const rects = laneRects(scene, widthPx);
  scene.lanes.forEach((lane, i) => {
    const rect = rects[i];
    if (rect === undefined) return;
    ctx.save();
    clipRect(ctx, rect.x0, 0, rect.x1 - rect.x0, axis.heightPx);
    try {
      drawLane(ctx, axis, lane, rect, theme, scene.compact);
    } finally {
      ctx.restore();
    }
  });
}

/**
 * The parts of a motion column pinned to the visible area (drawn over the cached layers with the live axis): per
 * lane the value-axis labels at the top, the lane label at the bottom and the "Not set" text of an absent track;
 * the pair link chain and the column message.
 */
export function drawMotionFixed(
  ctx: CanvasRenderingContext2D,
  axis: TimeAxis,
  widthPx: number,
  scene: MotionColumnScene,
  theme: MotionTheme,
): void {
  const rects = laneRects(scene, widthPx);
  const h = axis.heightPx;
  scene.lanes.forEach((lane, i) => {
    const rect = rects[i];
    if (rect === undefined || scene.compact) return;
    const w = rect.x1 - rect.x0;
    const map = rect.map;
    ctx.save();
    clipRect(ctx, rect.x0, 0, w, h);
    try {
      if (lane.keys.length === 0 && lane.absentText !== null) {
        ctx.font = canvasFont(theme.base, 10, 'sans');
        ctx.fillStyle = theme.label;
        wrapText(ctx, lane.absentText, (rect.x0 + rect.x1) / 2, h / 2, w - 6, 13);
      }
      if (map !== null && w >= 44 && lane.range !== null && lane.raw === null) {
        ctx.font = canvasFont(theme.base, LABEL_FONT_PX - 1, 'mono');
        const fit = fitRangeLabels(ctx, lane.format(map.lo), lane.format(map.hi), w - 4);
        plateText(ctx, fit.lo, rect.x0 + 1, 7, fit.loW, theme.label, theme.labelBg, 'left');
        plateText(ctx, fit.hi, rect.x1 - 1, 7, fit.hiW, theme.label, theme.labelBg, 'right');
      }
      if (lane.label !== null && lane.raw === null) {
        ctx.font = canvasFont(theme.base, 10, 'sans', 700);
        plateText(ctx, lane.label, (rect.x0 + rect.x1) / 2, h - 9, w, theme.keyStroke, theme.labelBg, 'center');
      }
    } finally {
      ctx.restore();
    }
  });
  if (scene.pairLinked !== null && rects.length === 2) {
    const a = rects[0];
    const b = rects[1];
    if (a !== undefined && b !== undefined) {
      const x = (a.x1 + b.x0) / 2;
      if (scene.pairLinked) chain(ctx, x, 10, theme.link);
      else {
        ctx.save();
        ctx.strokeStyle = theme.ghost;
        ctx.setLineDash([2, 2]);
        ctx.beginPath();
        ctx.moveTo(x - 3, 6);
        ctx.lineTo(x + 3, 14);
        ctx.stroke();
        ctx.restore();
      }
    }
  }
  if (scene.message !== null) {
    ctx.save();
    ctx.font = canvasFont(theme.base, 11, 'sans');
    ctx.fillStyle = theme.label;
    wrapText(ctx, scene.message, widthPx / 2, h / 2, widthPx - 8, 14);
    ctx.restore();
  }
}

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  cx: number,
  cy: number,
  maxW: number,
  lineH: number,
): void {
  const words = text.split(/\s+/).filter((w) => w !== '');
  const lines: string[] = [];
  let cur = '';
  for (const w of words) {
    const next = cur === '' ? w : `${cur} ${w}`;
    if (ctx.measureText(next).width <= maxW || cur === '') cur = next;
    else {
      lines.push(cur);
      cur = w;
    }
  }
  if (cur !== '') lines.push(cur);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const top = cy - ((lines.length - 1) * lineH) / 2;
  lines.forEach((l, i) => {
    ctx.fillText(fitText(ctx, l, maxW), cx, top + i * lineH);
  });
}

function drawLane(
  ctx: CanvasRenderingContext2D,
  axis: TimeAxis,
  lane: MotionLaneScene,
  rect: LaneRect,
  theme: MotionTheme,
  compact: boolean,
): void {
  const h = axis.heightPx;
  const w = rect.x1 - rect.x0;
  const map = rect.map;
  if (lane.focused) {
    ctx.save();
    ctx.globalAlpha = 0.18;
    ctx.fillStyle = theme.focus;
    ctx.fillRect(rect.x0, 0, w, h);
    ctx.restore();
  }

  // The part of the opacity axis below 0 (notes hidden there).
  if (map !== null && lane.negativeZone && map.lo < 0) {
    const zx = Math.max(rect.x0, Math.min(rect.x1, map.toX(0)));
    ctx.save();
    ctx.globalAlpha = 0.12;
    ctx.fillStyle = theme.dangerMuted;
    ctx.fillRect(rect.x0, 0, zx - rect.x0, h);
    ctx.restore();
  }
  if (map !== null && map.lo < 0 && map.hi > 0) {
    ctx.save();
    ctx.strokeStyle = theme.base.divider;
    ctx.setLineDash([2, 3]);
    ctx.beginPath();
    const zx = Math.round(map.toX(0)) + 0.5;
    ctx.moveTo(zx, 0);
    ctx.lineTo(zx, h);
    ctx.stroke();
    ctx.restore();
  }

  // Hatched bands: opacity < 0 hides the line's notes (still judged).
  for (const [s, e] of lane.hidden) {
    const yTop = axis.yAtBeat(e);
    const yBottom = axis.yAtBeat(s);
    if (yBottom < 0 || yTop > h) continue;
    ctx.save();
    ctx.strokeStyle = theme.danger;
    ctx.globalAlpha = 0.55;
    hatchRect(ctx, rect.x0, yTop, w, yBottom - yTop, 6);
    ctx.restore();
    if (!compact && yBottom - yTop >= 16 && lane.hiddenText !== '') {
      ctx.save();
      ctx.font = canvasFont(theme.base, LABEL_FONT_PX, 'sans', 600);
      for (const y of bandLabelRows(yTop, yBottom)) {
        if (y < -8 || y > h + 8) continue;
        plateText(ctx, lane.hiddenText, (rect.x0 + rect.x1) / 2, y, w - 4, theme.danger, theme.labelBg, 'center');
      }
      ctx.restore();
    }
  }

  // Colour bands.
  for (const band of lane.colorBands) {
    const yTop = axis.yAtBeat(band.beat1);
    const yBottom = axis.yAtBeat(band.beat0);
    if (yBottom < 0 || yTop > h) continue;
    ctx.fillStyle = rgbCss(band.rgb);
    const pad = Math.min(10, w / 5);
    ctx.fillRect(rect.x0 + pad, yTop, w - 2 * pad, Math.max(1, yBottom - yTop));
  }

  if (lane.raw !== null) {
    drawRawEvents(ctx, axis, lane, rect, theme);
    return;
  }

  const [i0, i1] = laneVisible(lane, axis);
  const keys = lane.keys;

  // Selected segments.
  for (let i = i0; i < i1 - 1; i++) {
    const k = keys[i];
    const n = keys[i + 1];
    if (k === undefined || n === undefined || !k.segmentSelected) continue;
    const yTop = axis.yAtBeat(n.beat);
    const yBottom = axis.yAtBeat(k.beat);
    ctx.save();
    ctx.globalAlpha = 0.45;
    ctx.fillStyle = theme.selection;
    ctx.fillRect(rect.x0, yTop, w, yBottom - yTop);
    ctx.restore();
    ctx.fillStyle = theme.keySelected;
    ctx.fillRect(rect.x0, yTop, 2, yBottom - yTop);
  }

  // Curves, back to front.
  if (map !== null) {
    let mainDrawn = false;
    for (const c of lane.curves) {
      ctx.save();
      if (c.style === 'guide') {
        ctx.strokeStyle = theme.curveGuide;
        ctx.lineWidth = 1;
        ctx.globalAlpha = 0.7;
      } else if (c.style === 'sigma') {
        ctx.strokeStyle = theme.curveGuide;
        ctx.lineWidth = 1.25;
        ctx.setLineDash([5, 3]);
      } else {
        ctx.strokeStyle = theme.curve;
        ctx.lineWidth = compact ? 1.25 : 1.75;
        mainDrawn = true;
      }
      strokeSampledCurve(ctx, c.curve, axis, (v) => map.toX(v));
      ctx.restore();
    }
    if (!mainDrawn) {
      // No sampled curve (no engine yet): straight segments between the keys.
      ctx.save();
      ctx.strokeStyle = theme.curve;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      for (let i = i0; i < i1 - 1; i++) {
        const k = keys[i];
        const n = keys[i + 1];
        if (k === undefined || n === undefined || k.ease.kind === 'hold') continue;
        ctx.moveTo(keyX(lane, rect, k, 'out'), axis.yAtBeat(k.beat));
        ctx.lineTo(keyX(lane, rect, n, 'in'), axis.yAtBeat(n.beat));
      }
      ctx.stroke();
      ctx.restore();
    }
  }

  // Stay segments (dashed) and jump steps.
  for (let i = i0; i < i1; i++) {
    const k = keys[i];
    if (k === undefined) continue;
    const y = axis.yAtBeat(k.beat);
    const n = keys[i + 1];
    if (n !== undefined && k.ease.kind === 'hold') {
      ctx.strokeStyle = theme.hold;
      ctx.lineWidth = 1.25;
      Glyphs.holdSegment(ctx, keyX(lane, rect, k, 'out'), y, axis.yAtBeat(n.beat));
    }
    if (k.jump && lane.kind === 'number') {
      ctx.save();
      ctx.strokeStyle = theme.hold;
      ctx.setLineDash([3, 2]);
      ctx.beginPath();
      ctx.moveTo(keyX(lane, rect, k, 'in'), y + 0.5);
      ctx.lineTo(keyX(lane, rect, k, 'out'), y + 0.5);
      ctx.stroke();
      ctx.restore();
    }
  }

  // Ghost key: Phira's default value before the first key (colour white, empty text).
  if (lane.ghostKey !== null) {
    const g = lane.ghostKey;
    const gy = axis.yAtBeat(g.beat);
    const gx = lane.kind === 'color' ? (rect.x0 + rect.x1) / 2 : rect.x0 + 12;
    if (gy >= -10 && gy <= h + 10) {
      ctx.save();
      ctx.strokeStyle = theme.ghost;
      ctx.setLineDash([2, 2]);
      if (g.rgb !== undefined) {
        ctx.fillStyle = rgbCss(g.rgb, 0.6);
        Glyphs.keyDiamond(ctx, gx, gy, MOTION_KEY_SIZE, true);
      } else Glyphs.keyDiamond(ctx, gx, gy, MOTION_KEY_SIZE, false);
      ctx.restore();
      if (!compact) {
        ctx.save();
        ctx.font = canvasFont(theme.base, LABEL_FONT_PX, 'sans');
        plateText(ctx, g.text, gx + 8, gy - 10, rect.x1 - gx - 8, theme.ghost, theme.labelBg, 'left');
        ctx.restore();
      }
    }
  }

  // Keys, labels, badges.
  ctx.save();
  ctx.font = canvasFont(theme.base, LABEL_FONT_PX, 'mono');
  for (let i = i0; i < i1; i++) {
    const k = keys[i];
    if (k === undefined) continue;
    const y = axis.yAtBeat(k.beat);
    const n = keys[i + 1];
    const segPx = n === undefined ? Number.POSITIVE_INFINITY : y - axis.yAtBeat(n.beat);
    // The previous segment's decor is drawn (and so its end label at this key) only inside the iterated range.
    const p = i > 0 ? keys[i - 1] : undefined;
    const prevSegPx = p === undefined || i - 1 < i0 ? 0 : axis.yAtBeat(p.beat) - y;
    if (!compact && n !== undefined) drawSegmentDecor(ctx, axis, lane, rect, theme, k, n, segPx, p, prevSegPx);
    drawKey(ctx, lane, rect, theme, k, y, compact);
    if (!compact && lane.kind === 'text') {
      const kx = keyX(lane, rect, k, 'out');
      plateText(
        ctx,
        textLabel(lane, k.textOut),
        kx + 7,
        y - 9,
        rect.x1 - kx - 8,
        theme.keyStroke,
        theme.labelBg,
        'left',
      );
      if (k.jump)
        plateText(ctx, textLabel(lane, k.textIn), kx + 7, y + 9, rect.x1 - kx - 8, theme.label, theme.labelBg, 'left');
    }
  }
  ctx.restore();

}

/** Label of a text keyframe value: formatted (quoted) by the scene's translated formatter. */
function textLabel(lane: MotionLaneScene, text: string | undefined): string {
  const raw = text ?? '';
  return lane.formatText === undefined ? raw : lane.formatText(raw);
}

function drawKey(
  ctx: CanvasRenderingContext2D,
  lane: MotionLaneScene,
  rect: LaneRect,
  theme: MotionTheme,
  k: MotionKeyView,
  y: number,
  compact: boolean,
): void {
  const size = compact ? MOTION_KEY_SIZE - 2 : MOTION_KEY_SIZE;
  if (lane.kind === 'color' && k.rgbOut !== undefined) {
    // Colour keys carry their colour inside the diamond (the outline says selected or not).
    const x = keyX(lane, rect, k, 'out');
    ctx.save();
    if (k.jump && k.rgbIn !== undefined) {
      ctx.fillStyle = rgbCss(k.rgbIn);
      ctx.strokeStyle = k.selected ? theme.keySelected : theme.keyStroke;
      Glyphs.jumpKey(ctx, x - 1, y, size + 2, true);
      ctx.fillStyle = rgbCss(k.rgbOut);
      Glyphs.jumpKey(ctx, x + 1, y, size + 2, false);
    } else {
      ctx.fillStyle = rgbCss(k.rgbOut);
      ctx.strokeStyle = k.selected ? theme.keySelected : theme.keyStroke;
      ctx.lineWidth = k.selected ? 2 : 1;
      Glyphs.keyDiamond(ctx, x, y, size + 2, true);
    }
    ctx.restore();
    return;
  }
  if (k.jump) {
    ctx.save();
    ctx.lineWidth = 1.25;
    ctx.fillStyle = k.selected ? theme.keySelected : theme.keyFill;
    ctx.strokeStyle = k.selected ? theme.keySelected : theme.curve;
    Glyphs.jumpKey(ctx, keyX(lane, rect, k, 'in'), y, size, true);
    Glyphs.jumpKey(ctx, keyX(lane, rect, k, 'out'), y, size, false);
    if (k.selected) {
      ctx.strokeStyle = theme.keyStroke;
      ctx.lineWidth = 1;
      Glyphs.jumpKey(ctx, keyX(lane, rect, k, 'in'), y, size + 3, true);
      Glyphs.jumpKey(ctx, keyX(lane, rect, k, 'out'), y, size + 3, false);
    }
    ctx.restore();
    return;
  }
  diamond(ctx, keyX(lane, rect, k, 'out'), y, k.selected, theme, size);
}

/** A value label: muted on a plate, emphasised (text colour, semibold) at a jump. */
function labelText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxW: number,
  theme: MotionTheme,
  align: 'left' | 'right',
  jump: boolean,
): void {
  if (!jump) {
    plateText(ctx, text, x, y, maxW, theme.label, theme.labelBg, align);
    return;
  }
  const font = ctx.font;
  ctx.font = canvasFont(theme.base, LABEL_FONT_PX, 'mono', 700);
  plateText(ctx, text, x, y, maxW, theme.keyStroke, theme.labelBg, align);
  ctx.font = font;
}

/**
 * Rows (y) of the label of a hatched band from yTop to yBottom: its middle, or for a band taller than
 * BAND_LABEL_EVERY_PX one label every BAND_LABEL_EVERY_PX from its start, so the positions depend on beats only.
 */
export function bandLabelRows(yTop: number, yBottom: number): number[] {
  const hgt = yBottom - yTop;
  if (hgt <= BAND_LABEL_EVERY_PX) return [(yTop + yBottom) / 2];
  const out: number[] = [];
  for (let y = yBottom - BAND_LABEL_EVERY_PX / 2; y > yTop + 8 && out.length < 400; y -= BAND_LABEL_EVERY_PX) out.push(y);
  return out;
}

/**
 * Value labels, link chain and badges of the segment k -> n (segPx = its height on screen); `prev` is the key
 * before k (its segment `prevSegPx` high), to print one label per continuous key.
 */
function drawSegmentDecor(
  ctx: CanvasRenderingContext2D,
  axis: TimeAxis,
  lane: MotionLaneScene,
  rect: LaneRect,
  theme: MotionTheme,
  k: MotionKeyView,
  n: MotionKeyView,
  segPx: number,
  prev: MotionKeyView | undefined,
  prevSegPx: number,
): void {
  const y0 = axis.yAtBeat(k.beat);
  const y1 = axis.yAtBeat(n.beat);
  const w = rect.x1 - rect.x0;
  if (lane.kind === 'number' && lane.valueLabels && segPx >= LABEL_SEGMENT_PX) {
    // One label per key (UX-032): the value a segment ends with is printed below its end key; the next segment's
    // start label above that key only says something new at a jump, where both are emphasised.
    const start = lane.format(k.vOut);
    const arrive = prev === undefined ? null : lane.format(prev.ease.kind === 'hold' ? prev.vOut : k.vIn);
    const prevLabelled = prev !== undefined && prevSegPx >= LABEL_SEGMENT_PX;
    const jumpAtStart = arrive !== null && arrive !== start;
    const end = lane.format(k.ease.kind === 'hold' ? k.vOut : n.vIn);
    const jumpAtEnd = Number.isFinite(n.vOut) && end !== lane.format(n.vOut);
    const sx = keyX(lane, rect, k, 'out');
    const ex = keyX(lane, rect, n, 'in');
    const mid = (rect.x0 + rect.x1) / 2;
    if (!prevLabelled || jumpAtStart) {
      labelText(ctx, start, sx < mid ? sx + 6 : sx - 6, y0 - 9, w / 2 + 6, theme, sx < mid ? 'left' : 'right', jumpAtStart);
    }
    labelText(ctx, end, ex < mid ? ex + 6 : ex - 6, y1 + 9, w / 2 + 6, theme, ex < mid ? 'left' : 'right', jumpAtEnd);
  }
  const ym = (y0 + y1) / 2;
  if (k.link !== 0 && segPx >= 10)
    chain(ctx, rect.x1 - 9, ym - (segPx >= BADGE_SEGMENT_PX + BADGE_H ? BADGE_H : 0), theme.link);
  const badges = lane.badges.get(k.id);
  if (badges === undefined || badges.length === 0 || segPx < BADGE_SEGMENT_PX) return;
  const fit = Math.max(1, Math.min(badges.length, Math.floor((segPx - 4) / (BADGE_H + 2))));
  const total = fit * (BADGE_H + 2) - 2;
  let y = ym - total / 2 + BADGE_H / 2;
  ctx.save();
  ctx.font = canvasFont(theme.base, LABEL_FONT_PX, 'sans', 600);
  for (let i = 0; i < fit; i++) {
    const b = badges[i];
    if (b === undefined) continue;
    const colors = theme.badge[b.tone];
    plateText(ctx, b.text, (rect.x0 + rect.x1) / 2, y, w - 4, colors.fg, colors.bg, 'center');
    y += BADGE_H + 2;
  }
  ctx.restore();
}

function drawRawEvents(
  ctx: CanvasRenderingContext2D,
  axis: TimeAxis,
  lane: MotionLaneScene,
  rect: LaneRect,
  theme: MotionTheme,
): void {
  const raw = lane.raw ?? [];
  const w = rect.x1 - rect.x0;
  const barX = rect.x0 + 4;
  ctx.save();
  ctx.font = canvasFont(theme.base, LABEL_FONT_PX, 'mono');
  raw.forEach((ev, i) => {
    const yTop = axis.yAtBeat(ev.beat1);
    const yBottom = axis.yAtBeat(ev.beat0);
    if (yBottom < 0 || yTop > axis.heightPx) return;
    ctx.fillStyle = i % 2 === 0 ? theme.curve : theme.curveGuide;
    ctx.globalAlpha = 0.85;
    ctx.fillRect(barX, yTop + 1, 4, Math.max(1, yBottom - yTop - 2));
    ctx.globalAlpha = 1;
    const tall = yBottom - yTop;
    if (tall >= 14) plateText(ctx, ev.values, barX + 7, yBottom - 7, w - 12, theme.keyStroke, theme.labelBg, 'left');
    if (tall >= 28) plateText(ctx, ev.easing, barX + 7, yBottom - 19, w - 12, theme.label, theme.labelBg, 'left');
    if (tall >= 42 && ev.note !== null)
      plateText(ctx, ev.note, barX + 7, yBottom - 31, w - 12, theme.link, theme.labelBg, 'left');
  });
  ctx.restore();
}

// ---------------------------------------------------------------------------------------------------------------
// Overlay layer
// ---------------------------------------------------------------------------------------------------------------

export function drawMotionOverlay(
  ctx: CanvasRenderingContext2D,
  axis: TimeAxis,
  widthPx: number,
  scene: MotionColumnScene,
  overlay: MotionOverlayScene,
  theme: MotionTheme,
): void {
  const rects = laneRects(scene, widthPx);
  const h = axis.heightPx;

  // Value at the playhead.
  for (const p of overlay.playhead) {
    const map = rects[p.lane]?.map ?? null;
    const rect = rects[p.lane];
    if (map === null || rect === undefined || !Number.isFinite(p.value)) continue;
    const x = map.toX(p.value);
    if (x < rect.x0 || x > rect.x1) continue;
    ctx.save();
    ctx.strokeStyle = theme.base.playhead;
    ctx.fillStyle = theme.keyFill;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(x, axis.yAtBeat(p.beat), 3.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  }

  // Hover ring.
  const hv = overlay.hover;
  if (hv !== null && hv.lane >= 0) {
    const lane = scene.lanes[hv.lane];
    const rect = rects[hv.lane];
    const k = lane?.keys[hv.index];
    if (lane !== undefined && rect !== undefined && k !== undefined) {
      ctx.save();
      ctx.strokeStyle = theme.keySelected;
      ctx.lineWidth = 1;
      if (hv.kind === 'segment') {
        const n = lane.keys[hv.index + 1];
        if (n !== undefined) {
          const yTop = axis.yAtBeat(n.beat);
          const yBottom = axis.yAtBeat(k.beat);
          ctx.globalAlpha = 0.6;
          ctx.strokeRect(rect.x0 + 0.5, yTop + 0.5, rect.x1 - rect.x0 - 1, yBottom - yTop - 1);
        }
      } else {
        const part = hv.kind === 'keyIn' ? 'in' : 'out';
        Glyphs.keyDiamond(ctx, keyX(lane, rect, k, part), axis.yAtBeat(k.beat), MOTION_KEY_SIZE + 6, false);
      }
      ctx.restore();
    }
  }

  // RPE placement ghost.
  const pl = overlay.placement;
  if (pl !== null) {
    const rect = rects[pl.lane];
    if (rect !== undefined) {
      const lo = Math.min(pl.beat0, pl.beat1);
      const hi = Math.max(pl.beat0, pl.beat1);
      const yTop = axis.yAtBeat(hi);
      const yBottom = axis.yAtBeat(lo);
      const color = pl.valid ? theme.keySelected : theme.danger;
      ctx.save();
      ctx.globalAlpha = 0.2;
      ctx.fillStyle = color;
      ctx.fillRect(rect.x0, yTop, rect.x1 - rect.x0, Math.max(2, yBottom - yTop));
      ctx.globalAlpha = 1;
      ctx.strokeStyle = color;
      ctx.lineWidth = 1.5;
      if (pl.constant) ctx.setLineDash([4, 3]);
      ctx.strokeRect(rect.x0 + 1, yTop, rect.x1 - rect.x0 - 2, Math.max(2, yBottom - yTop));
      ctx.setLineDash([]);
      ctx.fillStyle = theme.keyFill;
      Glyphs.keyDiamond(ctx, (rect.x0 + rect.x1) / 2, yBottom, MOTION_KEY_SIZE, true);
      ctx.font = canvasFont(theme.base, LABEL_FONT_PX, 'sans', 600);
      plateText(
        ctx,
        pl.text,
        (rect.x0 + rect.x1) / 2,
        Math.max(8, yTop - 8),
        rect.x1 - rect.x0,
        color,
        theme.labelBg,
        'center',
      );
      ctx.restore();
    }
  }

  // Paste ghosts.
  for (const g of overlay.ghosts) {
    const rect = rects[g.lane];
    const lane = scene.lanes[g.lane];
    if (rect === undefined || lane === undefined) continue;
    const xOf = (v: number): number =>
      rect.map !== null && Number.isFinite(v) ? rect.map.toX(v) : (rect.x0 + rect.x1) / 2;
    ctx.save();
    ctx.strokeStyle = theme.ghost;
    ctx.globalAlpha = 0.9;
    ctx.setLineDash([3, 3]);
    ctx.beginPath();
    g.points.forEach(([beat, v], i) => {
      const x = xOf(v);
      const y = axis.yAtBeat(beat);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.stroke();
    ctx.setLineDash([]);
    for (const [beat, v] of g.points) {
      const y = axis.yAtBeat(beat);
      if (y < -10 || y > h + 10) continue;
      Glyphs.keyDiamond(ctx, xOf(v), y, MOTION_KEY_SIZE, false);
    }
    ctx.restore();
  }

  // Dragged key readout.
  const dl = overlay.dragLabel;
  if (dl !== null) {
    const rect = rects[dl.lane];
    if (rect !== undefined) {
      const x = rect.map !== null && Number.isFinite(dl.value) ? rect.map.toX(dl.value) : (rect.x0 + rect.x1) / 2;
      const y = axis.yAtBeat(dl.beat);
      ctx.save();
      ctx.font = canvasFont(theme.base, 10, 'mono', 600);
      const right = x < (rect.x0 + rect.x1) / 2;
      plateText(
        ctx,
        dl.text,
        right ? x + 8 : x - 8,
        Math.max(8, y - 14),
        Math.max(40, widthPx),
        theme.keyStroke,
        theme.labelBg,
        right ? 'left' : 'right',
      );
      ctx.restore();
    }
  }
}
